import React, { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import nityaSevaAPI from "../api/nityaSevaAPI";
import { setAccessToken, clearAccessToken } from "../api/axiosInstance";

// ─── Nitya Seva Member Context ─────────────────────────────────────────────
// Single source of truth for "is a Nitya Sevak logged in right now?".
// Header (account dropdown) and the Dashboard page both read from here,
// so login state stays in sync across the whole site.

const NityaSevaMemberContext = createContext(null);

export function NityaSevaMemberProvider({ children }) {
  const [jwtToken, setJwtToken] = useState(
    () => localStorage.getItem("accessToken") || "",
  );
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // App load hote hi agar token localStorage mein mila, profile fetch kar lo
  useEffect(() => {
    if (jwtToken) {
      fetchProfile(jwtToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setSessionToken(token) {
    setJwtToken(token);
    setAccessToken(token);
    localStorage.setItem("accessToken", token);
  }

  async function fetchProfile(token) {
    setProfileLoading(true);
    try {
      const res = await nityaSevaAPI.getMemberProfile(token || jwtToken);
      setProfile(res.data);
    } catch (err) {
        toast.error(err?.response?.data?.message);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }

  // Login — email + password (the credentials emailed to the donor when
  // their account was first created). Primary login path on the website.
  async function loginWithPassword(email, password) {
    setAuthLoading(true);
    try {
      const res = await nityaSevaAPI.memberLoginPassword({ email, password });
      const token = res?.data?.tokens?.accessToken;
      if (!token) {
        toast.error(res?.data?.message || "Login failed. Please try again.");
        return false;
      }
      setSessionToken(token);
      toast.success(res?.data?.message || "Logged in successfully! Hare Krishna!");
      await fetchProfile(token);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid email or password.");
      return false;
    } finally {
      setAuthLoading(false);
    }
  }

  // Login Step 1 — mobile bhejo, OTP aayega
  async function login(mobile) {
    setAuthLoading(true);
    try {
      await nityaSevaAPI.memberLogin({ mobile });
      toast.success("OTP sent to your mobile!");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Login failed. Please try again.",);
      return false;
    } finally {
      setAuthLoading(false);
    }
  }

  // Login Step 2 — OTP verify karo, token milega
  async function verifyLoginOtp(mobile, otp) {
    setAuthLoading(true);
    try {
      const res = await nityaSevaAPI.memberVerifyOtp({ mobile, otp });
      // Backend field ka naam alag ho sakta hai — dono ko cover kar liya
      const token = res?.data?.tokens.accessToken;
      if (!token) {
        toast.error(res?.message || "Login failed. Please try again.");
        return false;
      }
      setSessionToken(token);
      // toast.success("Logged in successfully! Hare Krishna!");
      toast.success(res?.message || "Logged in successfully! Hare Krishna!");
      await fetchProfile(token);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Login failed. Please try again.");
      return false;
    } finally {
      setAuthLoading(false);
    }
  }

  async function resendLoginOtp(mobile) {
    try {
      await nityaSevaAPI.memberResendOtp({ mobile });
      toast.success("OTP resent to your number.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not resend OTP.");
    }
  }

  async function updateProfile(fields) {
    setAuthLoading(true);
    try {
      const res = await nityaSevaAPI.updateMemberProfile(fields, jwtToken);
      setProfile(res.data);
      toast.success("Profile updated successfully!");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update profile.");
      return false;
    } finally {
      setAuthLoading(false);
    }
  }

  async function logout() {
    try {
      await nityaSevaAPI.memberLogout(jwtToken);
    } catch (err) {
      // token already invalid/expired — proceed with local logout anyway
    }
    localStorage.removeItem("accessToken");
    clearAccessToken();
    setJwtToken("");
    setProfile(null);
    toast.success("Logged out successfully.");
  }

  return (
    <NityaSevaMemberContext.Provider
      value={{
        jwtToken,
        isLoggedIn: !!jwtToken,
        profile,
        profileLoading,
        authLoading,
        setSessionToken,
        fetchProfile,
        loginWithPassword,
        login,
        verifyLoginOtp,
        resendLoginOtp,
        updateProfile,
        logout,
      }}
    >
      {children}
    </NityaSevaMemberContext.Provider>
  );
}

export function useNityaSevaMember() {
  const ctx = useContext(NityaSevaMemberContext);
  if (!ctx) {
    throw new Error(
      "useNityaSevaMember must be used inside NityaSevaMemberProvider",
    );
  }
  return ctx;
}
